import { Link2, Repeat, Scale, ClipboardCheck } from "lucide-react";
import { MaskedLine, Reveal, SectionNumber } from "./Reveal";
import { scrollToSection } from "../lib/scroll";

const REASONS = [
    {
        icon: Link2,
        title: "Single Point of Accountability",
        desc: "One contract, one partner, one standard — across all four domains.",
    },
    {
        icon: Repeat,
        title: "Cross-Sector Synergies",
        desc: "Recovered outputs become inputs for the next domain. Value compounds.",
    },
    {
        icon: Scale,
        title: "Commercial + Environmental Returns",
        desc: "Every solution is built to perform on the P&L and for the planet.",
    },
    {
        icon: ClipboardCheck,
        title: "Execution Discipline",
        desc: "Consultancy-grade governance from strategy to daily operations.",
    },
];

const CHAIN = ["Waste & Facilities", "Valorization", "Edible Oils", "Advanced Materials"];

export const WhyNomas = () => (
    <section
        data-testid="why-nomas-section"
        className="relative overflow-hidden py-28 md:py-40"
    >
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
            <SectionNumber n="04" label="Why NOMAS" />
            <h2 className="font-display mb-8 max-w-4xl text-5xl font-light leading-[1.06] tracking-tighter text-[#0E1112] sm:text-6xl lg:text-7xl">
                <MaskedLine>Anyone can offer a service.</MaskedLine>
                <MaskedLine delay={0.15} className="text-[#C8102E]">
                    We connect the whole system.
                </MaskedLine>
            </h2>
            <Reveal delay={0.2}>
                <p className="max-w-xl text-sm leading-relaxed text-[#45543F] md:text-base">
                    Most providers solve one part. NOMAS connects all of
                    it — one integrated services and solutions provider.
                </p>
            </Reveal>

            <Reveal delay={0.25}>
                <div
                    data-testid="integrator-diagram"
                    className="mt-16 flex flex-col items-center gap-6 rounded-3xl border border-[#DCE5D4] bg-white p-8 md:p-12 lg:flex-row lg:gap-0"
                >
                    <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-1">
                        {CHAIN.map((c, i) => (
                            <div
                                key={c}
                                className="flex items-center gap-3 rounded-lg border border-[#DCE5D4] bg-[#F7F9F5] px-5 py-3.5"
                            >
                                <span className="font-display text-xs text-[#598B3B]">
                                    0{i + 1}
                                </span>
                                <span className="text-sm font-medium text-[#0E1112]">{c}</span>
                            </div>
                        ))}
                    </div>
                    <div className="relative hidden h-24 w-28 lg:block" aria-hidden="true">
                        {[0, 1, 2, 3].map((i) => (
                            <span
                                key={i}
                                className="animate-flow-x absolute left-0 h-px w-full"
                                style={{
                                    top: `${12 + i * 24}%`,
                                    backgroundImage:
                                        "linear-gradient(90deg, #598B3B 40%, transparent 0)",
                                    backgroundSize: "14px 1px",
                                }}
                            />
                        ))}
                    </div>
                    <div className="flex shrink-0 flex-col items-center gap-3">
                        <span className="relative flex h-24 w-24 items-center justify-center rounded-full bg-[#0E1112]">
                            <span className="animate-pulse-ring absolute inset-0 rounded-full border border-[#7CBF52]" />
                            <span className="font-display text-sm font-semibold tracking-[0.2em] text-white">
                                NOMAS
                            </span>
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#598B3B]">
                            Integrator
                        </span>
                    </div>
                    <div className="relative hidden h-24 w-28 lg:block" aria-hidden="true">
                        <span
                            className="animate-flow-x absolute left-0 top-1/2 h-px w-full"
                            style={{
                                backgroundImage:
                                    "linear-gradient(90deg, #598B3B 40%, transparent 0)",
                                backgroundSize: "14px 1px",
                            }}
                        />
                    </div>
                    <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-1">
                        {["Lower total cost of operations", "Higher recovery & diversion", "One dashboard of accountability"].map((o) => (
                            <div
                                key={o}
                                className="flex items-center gap-3 rounded-lg border border-[#7CBF52]/40 bg-[#7CBF52]/10 px-5 py-3.5"
                            >
                                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#598B3B]" />
                                <span className="text-sm font-medium text-[#0E1112]">{o}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </Reveal>

            <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {REASONS.map((r, i) => (
                    <Reveal key={r.title} delay={i * 0.08} className="h-full">
                        <div
                            data-testid={`why-card-${i + 1}`}
                            className="group flex h-full flex-col rounded-2xl border border-[#DCE5D4] bg-white p-8 transition-[border-color,transform] duration-400 hover:-translate-y-1.5 hover:border-[#598B3B]/50"
                        >
                            <r.icon
                                size={24}
                                strokeWidth={1.5}
                                className="text-[#598B3B] transition-transform duration-500 group-hover:-translate-y-1 group-hover:scale-110"
                            />
                            <h3 className="font-display mt-6 text-xl font-medium leading-snug tracking-tight text-[#0E1112]">
                                {r.title}
                            </h3>
                            <p className="mt-3 text-[13px] leading-relaxed text-[#5C6B57]">
                                {r.desc}
                            </p>
                        </div>
                    </Reveal>
                ))}
            </div>
            <Reveal delay={0.15}>
                <div className="mt-16 flex flex-col items-center gap-6" data-testid="why-cta">
                    <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#5C6B57]">
                        Ready when you are
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <button
                            onClick={() => scrollToSection("#contact")}
                            data-testid="why-cta-talk"
                            className="rounded-full bg-[#598B3B] px-8 py-3.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#4a7530]"
                        >
                            Talk to an Expert
                        </button>
                        <a
                            href="https://wa.me/971563141428?text=Hello%20NOMAS%2C%20I%20would%20like%20to%20discuss%20a%20sustainability%2Fproject%20opportunity."
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="why-cta-whatsapp"
                            className="rounded-full border border-[#598B3B]/60 px-8 py-3.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-[#0E1112] transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 hover:border-[#598B3B] hover:bg-[#598B3B]/10"
                        >
                            WhatsApp Us
                        </a>
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);
